import axios from 'axios';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const companyApi = axios.create({ baseURL: `${BASE}/company` });

companyApi.interceptors.request.use(cfg => {
  const token = localStorage.getItem('cp_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

companyApi.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('cp_token');
      localStorage.removeItem('cp_user');
      window.location.href = '/company/login';
    }
    return Promise.reject(err);
  }
);

// Auth
export const companyRegister = d  => companyApi.post('/register', d);
export const companyLogin    = d  => companyApi.post('/login', d);
export const getCompanyMe    = () => companyApi.get('/me');

// Team
export const getTeam         = ()  => companyApi.get('/team');
export const addTeamMember   = d   => companyApi.post('/team', d);
export const updateTeamMember = (id, d) => companyApi.patch(`/team/${id}`, d);
export const removeTeamMember = id => companyApi.delete(`/team/${id}`);

// Assessments
export const getAssessments    = (params) => companyApi.get('/assessments', { params });
export const createAssessment  = d        => companyApi.post('/assessments', d);
export const getAssessment     = id       => companyApi.get(`/assessments/${id}`);
export const updateAssessment  = (id, d)  => companyApi.patch(`/assessments/${id}`, d);
export const deleteAssessment  = id       => companyApi.delete(`/assessments/${id}`);

// Questions
export const addQuestion    = (aId, d)        => companyApi.post(`/assessments/${aId}/questions`, d);
export const updateQuestion = (aId, qId, d)   => companyApi.patch(`/assessments/${aId}/questions/${qId}`, d);
export const deleteQuestion = (aId, qId)      => companyApi.delete(`/assessments/${aId}/questions/${qId}`);

// Phase 2: Category configurator + video flags
export const planCategories    = (id)        => companyApi.post(`/assessments/${id}/plan-categories`);
export const saveCategories    = (id, d)     => companyApi.patch(`/assessments/${id}/categories`, d);
export const setVideoFlags     = (id, ids)   => companyApi.patch(`/assessments/${id}/video-flags`, { question_ids: ids });

// Phase 4: Video signed token + audit log + billing
export const getVideoToken     = (aId, sId, anId) => companyApi.get(`/assessments/${aId}/sessions/${sId}/answers/${anId}/video-token`);
export const getVideoAuditLog  = (aId, sId, anId) => companyApi.get(`/assessments/${aId}/sessions/${sId}/answers/${anId}/video-audit`);
export const getVideoBilling   = () => companyApi.get('/video-billing');

// Workflow
export const getApprovers         = ()       => companyApi.get('/assessments/approvers');
export const submitForApproval    = (id, fd) => companyApi.patch(`/assessments/${id}/submit-approval`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const approveAssessment    = (id, d)  => companyApi.patch(`/assessments/${id}/approve`, d);
export const getApprovalLog       = id       => companyApi.get(`/assessments/${id}/approval-log`);
export const getApprovalJourney   = id       => companyApi.get(`/assessments/${id}/approval-journey`);
export const rejectAssessment     = (id, d)  => companyApi.patch(`/assessments/${id}/reject`, d);
export const overrideAssessment   = id        => companyApi.patch(`/assessments/${id}/override`);

// Chat (supports group and DM)
export const getChatMessages  = (recipientId, before) => companyApi.get('/chat', { params: { recipient_id: recipientId || undefined, before, limit: 50 } });
export const sendChatMessage  = (fd) => companyApi.post('/chat', fd, { headers: { 'Content-Type': 'multipart/form-data' } });

// Candidate Pool (Talent Pool)
export const getTalentPool        = (params) => companyApi.get('/candidates', { params });
export const updateTalentCandidate      = (id, data) => companyApi.patch(`/candidates/${id}`, data);
export const bulkDeleteCandidates = (ids)       => companyApi.post('/candidates/bulk-delete', { ids });
export const bulkStatusCandidates = (ids, status) => companyApi.post('/candidates/bulk-status', { ids, status });
export const bulkInviteCandidates = (ids, assessment_id) => companyApi.post('/candidates/bulk-invite', { ids, assessment_id });

// CV Scanning (standalone)
export const getCvScanResults      = ()         => companyApi.get('/cv-scan');
export const uploadCvsForScan      = (fd)       => companyApi.post('/cv-scan/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateCvScanResult    = (id, data) => companyApi.patch(`/cv-scan/${id}`, data);
export const deleteCvScanResult    = (id)       => companyApi.delete(`/cv-scan/${id}`);

// Notifications
export const getNotifications     = ()   => companyApi.get('/notifications?limit=20');
export const markAllNotifsRead    = ()   => companyApi.patch('/notifications/read-all');
export const markNotifRead        = (id) => companyApi.patch(`/notifications/${id}/read`);
export const closeAssessment    = id       => companyApi.patch(`/assessments/${id}/close`);
export const reopenAssessment   = id       => companyApi.patch(`/assessments/${id}/reopen`);
export const duplicateAssessment = id      => companyApi.post(`/assessments/${id}/duplicate`);

// Invitations + reports
export const inviteCandidates   = (id, d) => companyApi.post(`/assessments/${id}/invite`, d);
export const getCandidates      = id      => companyApi.get(`/assessments/${id}/candidates`);
export const getCandidateReport = (aId, sId) => companyApi.get(`/assessments/${aId}/candidates/${sId}`);
export const getCandidateDetail = (aId, sId) => companyApi.get(`/assessments/${aId}/candidates/${sId}`);

// Activation & pricing
export const activateCoupon    = (coupon_code) => companyApi.post('/activate-coupon', { coupon_code });
export const getCompanyPricing = () => companyApi.get('/pricing');

// Profile
export const getCompanyProfile     = ()  => companyApi.get('/profile');
export const updateCompanyProfile  = (d) => companyApi.patch('/profile', d);
export const changeCompanyPassword = (d) => companyApi.patch('/profile/password', d);
export const getProfileCompleteness = () => companyApi.get('/profile/completeness');
export const sendContactOtp        = (contact_email) => companyApi.post('/profile/contact-otp/send', { contact_email });
export const verifyContactOtp      = (otp) => companyApi.post('/profile/contact-otp/verify', { otp });
export const uploadCompanyCover    = (fd) => companyApi.post('/profile/upload-cover', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const uploadCompanyLogo     = (fd) => companyApi.post('/profile/upload-logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const uploadRegDoc          = (fd) => companyApi.post('/profile/upload-reg-doc', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const publishCareersPage    = () => companyApi.post('/profile/publish-page');
export const unpublishCareersPage  = () => companyApi.delete('/profile/publish-page');
export const markWizardShown       = () => companyApi.post('/profile/wizard-shown');

// Credits & subscription
export const getCredits         = ()             => companyApi.get('/credits');
export const topupCredits       = (type, qty)    => companyApi.post('/credits/topup', { type, quantity: qty });
export const updateSubscription  = (d)    => companyApi.patch('/subscription', d);
export const scheduleReduction   = (d)    => companyApi.post('/subscription/schedule-reduction', d);
export const cancelReduction     = ()     => companyApi.delete('/subscription/schedule-reduction');
export const redeemCoupon        = (code) => companyApi.post('/redeem-coupon', { code });

// Legacy billing (usage stats)
export const getUsage = () => companyApi.get('/usage');

// ── Recruit — Jobs ────────────────────────────────────────────────────────────
const recruitApi = axios.create({ baseURL: `${BASE}/company/recruit` });
recruitApi.interceptors.request.use(cfg => {
  const token = localStorage.getItem('cp_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});
recruitApi.interceptors.response.use(res => res, err => {
  if (err.response?.status === 401) {
    localStorage.removeItem('cp_token');
    localStorage.removeItem('cp_user');
    window.location.href = '/company/login';
  }
  return Promise.reject(err);
});

export const extractJdText           = (fd)           => recruitApi.post('/extract-jd', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getJobs                 = ()              => recruitApi.get('/jobs');
export const createJob               = (d)             => recruitApi.post('/jobs', d);
export const getJob                  = (id)            => recruitApi.get(`/jobs/${id}`);
export const updateJob               = (id, d)         => recruitApi.patch(`/jobs/${id}`, d);
export const publishJob              = (id)            => recruitApi.post(`/jobs/${id}/publish`);
export const closeJob                = (id)            => recruitApi.post(`/jobs/${id}/close`);
export const reopenJob               = (id)            => recruitApi.post(`/jobs/${id}/reopen`);
export const deleteJob               = (id)            => recruitApi.delete(`/jobs/${id}`);

export const getApplications         = (jobId, params) => recruitApi.get(`/jobs/${jobId}/applications`, { params });
export const getApplication          = (jobId, appId)  => recruitApi.get(`/jobs/${jobId}/applications/${appId}`);
export const downloadCv = async (jobId, appId, filename) => {
  const r = await recruitApi.get(`/jobs/${jobId}/applications/${appId}/cv`, { responseType: 'blob' });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a');
  a.href = url; a.download = filename || 'cv.pdf'; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};
export const downloadAllCvs = async (jobId, ids) => {
  const params = ids?.length ? `?ids=${ids.join(',')}` : '';
  let r;
  try {
    r = await recruitApi.get(`/jobs/${jobId}/cv-download${params}`, { responseType: 'blob' });
  } catch (e) {
    // Blob error responses need manual parsing
    if (e.response?.data instanceof Blob) {
      try {
        const text = await e.response.data.text();
        const json = JSON.parse(text);
        throw new Error(json.error || 'Download failed');
      } catch (parseErr) { throw parseErr; }
    }
    throw e;
  }
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a');
  a.href = url; a.download = `cvs-${Date.now()}.zip`; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};
export const scanCvs                 = (jobId, d)      => recruitApi.post(`/jobs/${jobId}/scan`, d);
export const getScanStatus           = (jobId)         => recruitApi.get(`/jobs/${jobId}/scan-status`);
export const bulkSendAssessment      = (jobId, d)      => recruitApi.post(`/jobs/${jobId}/bulk-send-assessment`, d);
export const evaluateCandidates      = (jobId)         => recruitApi.post(`/jobs/${jobId}/evaluate`);
export const shortlistApplication    = (jobId, appId)  => recruitApi.patch(`/jobs/${jobId}/applications/${appId}/shortlist`);
export const queueRejection          = (jobId, appId)  => recruitApi.patch(`/jobs/${jobId}/applications/${appId}/reject-queue`);
export const undoRejection           = (jobId, appId)  => recruitApi.patch(`/jobs/${jobId}/applications/${appId}/reject-undo`);
export const bulkAction              = (jobId, d)      => recruitApi.post(`/jobs/${jobId}/bulk-action`, d);
export const getPendingRejections    = (jobId)         => recruitApi.get(`/jobs/${jobId}/pending-rejections`);
export const sendPendingRejections   = (jobId)         => recruitApi.post(`/jobs/${jobId}/send-rejections`);
export const sendAssessment          = (jobId, appId, d) => recruitApi.post(`/jobs/${jobId}/applications/${appId}/send-assessment`, d);
export const applyExistingScore      = (jobId, appId, session_id) => recruitApi.post(`/jobs/${jobId}/applications/${appId}/use-existing-score`, { session_id });
export const nudgeCandidate          = (jobId, appId)  => recruitApi.post(`/jobs/${jobId}/applications/${appId}/nudge`);
export const sendInterviewInvite     = (jobId, appId, d) => recruitApi.post(`/jobs/${jobId}/applications/${appId}/interview`, d);
export const hireCandidate           = (jobId, appId)  => recruitApi.post(`/jobs/${jobId}/applications/${appId}/hire`);
export const getNotificationPrefs    = ()              => recruitApi.get('/notification-preferences');
export const updateNotificationPrefs = (preference)   => recruitApi.patch('/notification-preferences', { preference });
export const getJobSla               = (jobId)         => recruitApi.get(`/jobs/${jobId}/sla`);
export const updateJobSla            = (jobId, sla_days) => recruitApi.patch(`/jobs/${jobId}/sla`, { sla_days });
export const getSlaAlerts            = ()              => recruitApi.get('/sla/alerts');
export const getDiversityReport      = ()              => recruitApi.get('/diversity');
export const getDuplicates           = (jobId, appId)  => recruitApi.get(`/jobs/${jobId}/applications/${appId}/duplicates`);

// Re-engagement campaigns
export const getInactiveCandidates   = (months)        => companyApi.get('/candidates/inactive', { params: { months } });
export const reEngageCandidates      = (ids, subject, message) => companyApi.post('/candidates/re-engage', { ids, subject, message });

// Public apply API (no auth)
const applyApi = axios.create({ baseURL: BASE });
export const getPublicJob              = (co, job)  => applyApi.get(`/apply/${co}/${job}`);
export const submitApplication         = (co, job, fd) => applyApi.post(`/apply/${co}/${job}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getTrackingStatus         = (token)  => applyApi.get(`/apply/track/${token}`);
export const getPublicKnockoutQuestions = (co, job) => applyApi.get(`/apply/${co}/${job}/knockout-questions`);

// Public careers API (no auth)
const publicApi = axios.create({ baseURL: BASE });
export const getPublicCareers        = (params) => publicApi.get('/public/careers', { params });
export const getPublicCompany        = (slug)   => publicApi.get(`/public/companies/${slug}`);
export const getPublicCompanyJob     = (co, job) => publicApi.get(`/public/companies/${co}/jobs/${job}`);

// Assessment actions with reason/levels
export const closeAssessmentWithReason  = (id, d)   => companyApi.patch(`/assessments/${id}/close`, d);
export const reopenAssessmentAction     = id         => companyApi.patch(`/assessments/${id}/reopen`);
export const scheduleAssessmentClose    = (id, d)   => companyApi.patch(`/assessments/${id}/schedule-close`, d);

// NPS
export const sendNpsSurvey  = (d) => companyApi.post('/nps/send', d);
export const getNpsResults  = ()  => companyApi.get('/nps');

// Audit trail
export const getAuditLog = (params) => companyApi.get('/audit-log', { params });

// Candidate blacklist
export const getBlacklist     = ()        => companyApi.get('/blacklist');
export const addToBlacklist   = (d)       => companyApi.post('/blacklist', d);
export const removeFromBlacklist = (id)   => companyApi.delete(`/blacklist/${id}`);

// Collaborative notes
export const getNotes    = (params)   => companyApi.get('/notes', { params });
export const addNote     = (d)        => companyApi.post('/notes', d);
export const editNote    = (id, d)    => companyApi.patch(`/notes/${id}`, d);
export const deleteNote  = (id)       => companyApi.delete(`/notes/${id}`);

// Candidate tags
export const updateCandidateTags = (id, tags) => companyApi.patch(`/candidates/${id}/tags`, { tags });

// Knockout questions
export const getKnockoutQuestions  = (jobId)    => companyApi.get(`/recruit/jobs/${jobId}/knockout-questions`);
export const saveKnockoutQuestions = (jobId, d) => companyApi.put(`/recruit/jobs/${jobId}/knockout-questions`, d);

// Interview scorecards
export const getScorecards  = (appId) => companyApi.get(`/scorecards/${appId}`);
export const addScorecard   = (d)     => companyApi.post('/scorecards', d);

// Hiring requests
export const getHiringRequests    = (params)    => companyApi.get('/hiring-requests', { params });
export const createHiringRequest  = (d)         => companyApi.post('/hiring-requests', d);
export const updateHiringRequest  = (id, d)     => companyApi.patch(`/hiring-requests/${id}`, d);

// Onboarding checklists
export const getOnboarding    = (appId) => companyApi.get(`/onboarding/${appId}`);
export const createOnboarding = (d)     => companyApi.post('/onboarding', d);
export const updateOnboarding = (id, d) => companyApi.patch(`/onboarding/${id}`, d);

// Probation tracker
export const getProbation    = (appId) => companyApi.get(`/probation/${appId}`);
export const createProbation = (d)     => companyApi.post('/probation', d);
export const updateProbation = (id, d) => companyApi.patch(`/probation/${id}`, d);

// Reference checks
export const getReferenceChecks   = (appId) => companyApi.get(`/reference-checks/${appId}`);
export const sendReferenceCheck   = (d)     => companyApi.post('/reference-checks', d);

// Candidate NPS
export const sendNps  = (d)  => companyApi.post('/nps/send', d);
export const getNps   = ()   => companyApi.get('/nps');

// Interview scheduling
export const getInterviews        = ()       => companyApi.get('/interviews');
export const getInterviewSlots    = (appId)  => companyApi.get(`/interview-slots/${appId}`);
export const createInterviewSlots = (d)      => companyApi.post('/interview-slots', d);
export const confirmInterviewSlot = (id, d)  => companyApi.patch(`/interview-slots/${id}/confirm`, d);

// Offer letters
export const getOfferLetters  = (appId)  => companyApi.get(`/offer-letters/${appId}`);
export const createOfferLetter = (d)     => companyApi.post('/offer-letters', d);
export const sendOfferLetter   = (id)    => companyApi.patch(`/offer-letters/${id}/send`);

// AI tools
export const aiWriteJd             = (d) => companyApi.post('/ai/write-jd', d);
export const aiSuggestQuestions    = (d) => companyApi.post('/ai/interview-questions', d);
export const aiScorecardCriteria   = (d) => companyApi.post('/ai/scorecard-criteria', d);

// Session-based scorecards
export const getScorecardsBySession = (sessionId) => companyApi.get(`/scorecards/session/${sessionId}`);

// Score calibration
export const addCalibration   = (d)         => companyApi.post('/calibrations', d);
export const getCalibration   = (sessionId) => companyApi.get(`/calibrations/${sessionId}`);

// Job review links (external hiring-manager access)
export const getJobReviewLinks    = (jobId)           => companyApi.get(`/recruit/jobs/${jobId}/review-links`);
export const createJobReviewLink  = (jobId, d)        => companyApi.post(`/recruit/jobs/${jobId}/review-link`, d);
export const deleteJobReviewLink  = (jobId, linkId)   => companyApi.delete(`/recruit/jobs/${jobId}/review-links/${linkId}`);

// Talent community
export const getTalentCommunity   = ()       => companyApi.get('/talent-community');
export const notifyTalentCommunity = (d)     => companyApi.post('/talent-community/notify', d);

// Webhooks
export const getWebhooks    = ()       => companyApi.get('/webhooks');
export const createWebhook  = (d)      => companyApi.post('/webhooks', d);
export const updateWebhook  = (id, d)  => companyApi.patch(`/webhooks/${id}`, d);
export const deleteWebhook  = (id)     => companyApi.delete(`/webhooks/${id}`);

// Departments
export const getDepartments    = ()       => companyApi.get('/departments');
export const createDepartment  = (d)      => companyApi.post('/departments', d);
export const updateDepartment  = (id, d)  => companyApi.patch(`/departments/${id}`, d);
export const deleteDepartment  = (id)     => companyApi.delete(`/departments/${id}`);

// GDPR
export const gdprErasure = (d)     => companyApi.post('/gdpr/erasure', d);
export const gdprExport  = (email) => companyApi.get('/gdpr/export', { params: { email } });

// Employer brand analytics
export const getBrandAnalytics = () => companyApi.get('/brand-analytics');

// Company-wide search
export const globalSearch = (q) => companyApi.get('/search', { params: { q } });

// Analytics dashboard
export const getCompanyAnalytics = (params) => companyApi.get('/analytics', { params });

// Candidate comparison
export const compareCandidates = (ids) => companyApi.post('/candidates/compare', { ids });

// Feedback report — send to candidate
export const sendFeedbackReport = (assessmentId, sessionId) =>
  companyApi.post(`/assessments/${assessmentId}/sessions/${sessionId}/send-report`);

// Email templates
export const getEmailTemplates    = ()       => companyApi.get('/email-templates');
export const createEmailTemplate  = (d)      => companyApi.post('/email-templates', d);
export const updateEmailTemplate  = (id, d)  => companyApi.patch(`/email-templates/${id}`, d);
export const deleteEmailTemplate  = (id)     => companyApi.delete(`/email-templates/${id}`);

// Pipeline templates
export const getPipelineTemplates    = ()      => companyApi.get('/pipeline-templates');
export const createPipelineTemplate  = (d)     => companyApi.post('/pipeline-templates', d);
export const deletePipelineTemplate  = (id)    => companyApi.delete(`/pipeline-templates/${id}`);

// Automation rules
export const getAutomationRules    = ()        => companyApi.get('/automation-rules');
export const createAutomationRule  = (d)       => companyApi.post('/automation-rules', d);
export const updateAutomationRule  = (id, d)   => companyApi.patch(`/automation-rules/${id}`, d);
export const deleteAutomationRule  = (id)      => companyApi.delete(`/automation-rules/${id}`);

// Post-hire (new additions — existing getOnboarding/getProbation etc. are defined above)
export const getPostHire         = ()        => companyApi.get('/post-hire');
export const saveOfferLetter     = (d)       => companyApi.post('/offer-letters', d);
export const emailOfferLetter    = (id)      => companyApi.post(`/offer-letters/${id}/email`);

// Referrals
export const getReferrals  = ()   => companyApi.get('/referrals');
export const addReferral   = (d)  => companyApi.post('/referrals', d);

// Approval templates
export const getApprovalTemplates    = ()      => companyApi.get('/approval-templates');
export const createApprovalTemplate  = (d)     => companyApi.post('/approval-templates', d);
export const deleteApprovalTemplate  = (id)    => companyApi.delete(`/approval-templates/${id}`);

// CV batch management
export const getCvBatches      = (params)    => companyApi.get('/cv-scan/batches', { params });
export const updateCvBatch     = (bId, d)    => companyApi.patch(`/cv-scan/batches/${bId}`, d);
export const deleteCvBatch     = (bId)       => companyApi.delete(`/cv-scan/batches/${bId}`);

// Proctoring events (view for recruiter)
export const getProctoringEvents = (sessionId) => companyApi.get(`/proctoring/${sessionId}`);

// ── Video storage ─────────────────────────────────────────────────────────────
export const getVideoStorage = () => companyApi.get('/video-storage');

// ── Live interview rooms ──────────────────────────────────────────────────────
export const getInterviewRooms  = ()       => companyApi.get('/interview-rooms');
export const createInterviewRoom = (data)  => companyApi.post('/interview-rooms', data);
export const getInterviewRoom   = (id)     => companyApi.get(`/interview-rooms/${id}`);
export const patchInterviewRoom = (id, d)  => companyApi.patch(`/interview-rooms/${id}`, d);
export const uploadRecording    = (id, fd) => companyApi.post(`/interview-rooms/${id}/recording`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
export const getInterviewJoin   = (token)  => axios.get(`${BASE}/company/interview-join/${token}`);

// Candidate public API (no auth)
const candidateApi = axios.create({ baseURL: `${BASE}/candidate` });
export const getAssessmentByToken = token => candidateApi.get(`/assess/${token}`);
export const requestOTP           = token => candidateApi.post(`/assess/${token}/request-otp`);
export const verifyOTP            = (token, otp) => candidateApi.post(`/assess/${token}/verify-otp`, { otp });
export const startSession         = (token, fd)  => candidateApi.post(`/assess/${token}/start`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getQuestions         = token => candidateApi.get(`/assess/${token}/questions`);
export const submitAnswer         = (token, fd)  => candidateApi.post(`/assess/${token}/answers`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const completeAssessment   = (token, session_id) => candidateApi.post(`/assess/${token}/complete`, { session_id });
export const flagProctoring       = (token, d) => candidateApi.post(`/assess/${token}/proctoring`, d);

// Salary benchmarking
export const aiSalaryBenchmark = (d) => companyApi.post('/ai/salary-benchmark', d);

// Skills Test Library
export const getSkillsTests     = ()        => companyApi.get('/skills-tests');
export const getSkillsTest      = (id)      => companyApi.get(`/skills-tests/${id}`);
export const createSkillsTest   = (d)       => companyApi.post('/skills-tests', d);
export const updateSkillsTest   = (id, d)   => companyApi.patch(`/skills-tests/${id}`, d);
export const deleteSkillsTest   = (id)      => companyApi.delete(`/skills-tests/${id}`);

// 2FA
export const get2faStatus     = ()      => companyApi.get('/2fa/status');
export const setup2fa         = ()      => companyApi.post('/2fa/setup');
export const enable2fa        = (code)  => companyApi.post('/2fa/enable', { code });
export const disable2fa       = (code)  => companyApi.post('/2fa/disable', { code });
export const verify2faLogin   = (d)     => companyApi.post('/2fa/verify-login', d);

export const getWhitelabel    = ()      => companyApi.get('/whitelabel');
export const updateWhitelabel = (d)     => companyApi.patch('/whitelabel', d);

export default companyApi;
